import React, { useState, Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowLeftIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import RichTextEditor from '../../components/RichTextEditor';
import { dateToUTCNoon } from '../../utils/dateHelpers';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { trackProblemOperation } from '../../utils/appInsights';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { hasActualTextContent } from '../../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';

interface FormData {
  shortDescription: string;
  problemStatement: string;
  contactId: string;
  customerId: string;
  engagementId: string;
  resourceId: string;
  otherResourceNames: string;
  assignmentGroupId: string;
  assignedTo: string;
  impact: string; // Hidden field for priority calculation
  urgency: string; // Hidden field for priority calculation
  ticketType: string;
  // Review Matrix fields
  rcaPresentedById: string;
  reportedIssue: string;
  problemDate: string;
  actualServiceImpact: string;
  briefAnalysis: string;
  rootCause: string;
  detailedAnalysis: string;
  correctiveActions: string;
  chronologyOfEvents: string;
}

const ProblemCreate: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('problems', 'create');
  const { getToday } = useDateFormatter();
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();

  const isRichTextEmpty = (content: string): boolean => !hasActualTextContent(content);

  // Priority calculation function (matches backend logic)
  const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
    if (!impact || !urgency) return 'low_4'; // Default to Low

    const impactNum = parseInt(impact.split('_')[1]);
    const urgencyNum = parseInt(urgency.split('_')[1]);

    if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
    if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
    if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
    if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
    if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

    return 'low_4'; // Default to Low
  };

  const [formData, setFormData] = useState<FormData>({
    shortDescription: '',
    problemStatement: '',
    contactId: '',
    customerId: '',
    engagementId: '',
    resourceId: '',
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: '',
    impact: 'medium_2', // Hidden field for priority calculation
    urgency: 'medium_2', // Hidden field for priority calculation
    ticketType: 'PROBLEM',
    // Review Matrix fields
    rcaPresentedById: '',
    reportedIssue: '',
    problemDate: '',
    actualServiceImpact: '',
    briefAnalysis: '',
    rootCause: '',
    detailedAnalysis: '',
    correctiveActions: '',
    chronologyOfEvents: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);
  const [callerQuery, setCallerQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [rcaPresentedByQuery, setRcaPresentedByQuery] = useState('');

  // Fetch contacts for Reported By dropdown - filtered by customer
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.customerId, callerQuery],
    queryFn: async () => {
      if (!formData.customerId) return { users: [] };
      const params = new URLSearchParams({
        class: 'contact',
        customerId: formData.customerId,
        limit: '100'
      });
      if (callerQuery) {
        params.append('search', callerQuery);
      }
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch all users for RCA Presented By dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users?limit=1000');
      return response.data;
    }
  });

  // Fetch group members for assignment dropdown - only when assignment group is selected
  const { data: groupMembersData, isLoading: isLoadingMembers, error: membersError } = useQuery({
    queryKey: ['group-members', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      if (!formData.assignmentGroupId) return [];
      console.log('Fetching members for group:', formData.assignmentGroupId);
      try {
        const members = await fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
        console.log('Group members response:', members);
        return members || [];
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch assignment groups for dropdown (only groups with type 'assignment')
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      try {
        const response = await api.get('/groups/assignment-groups');
        console.log('Assignment groups response:', response.data);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching assignment groups:', error);
        return [];
      }
    }
  });

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements for dropdown - only when customer is selected
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return { engagements: [] };
      const response = await api.get(`/engagements?customerId=${formData.customerId}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch primary contact for auto-fill when customer/engagement is selected
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Auto-fill contact when customer/engagement is selected and primary contact is found
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  // Fetch resources for dropdown - only when engagement is selected
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return { resources: [], total: 0 };
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return response.data;
    },
    enabled: !!formData.engagementId
  });

  const createProblemMutation = useMutation({
    mutationFn: async (data: FormData) => {
      // Add ticketType to the data
      const submitData = { ...data, ticketType: 'PROBLEM' };
      const response = await api.post('/cases', submitData);
      return response.data;
    },
    onSuccess: async (data) => {
      // Invalidate list queries so they refetch when user navigates back
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['problems'] }),
        queryClient.invalidateQueries({ queryKey: ['cases'] })
      ]);
      
      // Log Problem Created
      if (user) {
        const problemNumber = data?.case?.number || data?.number || data?.id || data?._id || 'Unknown';
        trackProblemOperation(
          'Problem Created',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Problem Created',
            problemNumber: problemNumber,
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      // Support both id and _id from backend
      const newId = data.id || data._id;
      navigate(`/problems/${newId}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create problem';
      setErrors({ submit: message });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Record<string, string> = {};
    if (isRichTextEmpty(formData.shortDescription)) {
      newErrors.shortDescription = 'Short Description is required';
    }
    if (isRichTextEmpty(formData.problemStatement)) {
      newErrors.problemStatement = 'Problem Statement is required';
    }
    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      newErrors.engagementId = 'Engagement is required';
    }
    // Resource validation: either select a resource OR enter custom resource names
    if (!formData.resourceId && !isOtherResource) {
      if (!formData.engagementId) {
        newErrors.resourceId = 'Please select an engagement first to choose a resource';
      } else {
        newErrors.resourceId = 'Resource is required (select a resource or choose "Others")';
      }
    }
    if (isOtherResource && !formData.otherResourceNames.trim()) {
      newErrors.otherResourceNames = 'Please enter at least one custom resource name';
    }
    if (!formData.rcaPresentedById) {
      newErrors.rcaPresentedById = 'RCA Presented By is required';
    }
    if (!formData.problemDate) {
      newErrors.problemDate = 'Problem Date is required';
    } else {
      // Check if problem date is in the future using user's timezone preference
      // formData.problemDate is in YYYY-MM-DD format, compare against getToday() which uses user's timezone
      const selectedDateStr = formData.problemDate.split('T')[0]; // Extract date part only
      const todayStr = getToday();

      if (selectedDateStr > todayStr) {
        newErrors.problemDate = 'Problem Date cannot be in the future';
      }
    }
    if (isRichTextEmpty(formData.reportedIssue)) {
      newErrors.reportedIssue = 'Reported Issue is required';
    }
    if (isRichTextEmpty(formData.actualServiceImpact)) {
      newErrors.actualServiceImpact = 'Actual Service Impact is required';
    }
    if (isRichTextEmpty(formData.briefAnalysis)) {
      newErrors.briefAnalysis = 'Brief Analysis is required';
    }
    if (isRichTextEmpty(formData.rootCause)) {
      newErrors.rootCause = 'Root Cause is required';
    }
    if (isRichTextEmpty(formData.detailedAnalysis)) {
      newErrors.detailedAnalysis = 'Detailed Analysis is required';
    }
    if (isRichTextEmpty(formData.correctiveActions)) {
      newErrors.correctiveActions = 'Corrective/Mitigate Actions is required';
    }
    if (isRichTextEmpty(formData.chronologyOfEvents)) {
      newErrors.chronologyOfEvents = 'Chronology of Events is required';
    }
    if (!formData.assignmentGroupId) {
      newErrors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to the first error field
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }
    
    // Calculate priority from hidden impact and urgency
    const calculatedPriority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
    const submitData: any = {
      shortDescription: formData.shortDescription,
      problemStatement: formData.problemStatement,
      impact: formData.impact,
      urgency: formData.urgency,
      priority: calculatedPriority,
      category: 'issue',
      channel: 'web',
      ticketType: 'PROBLEM',
      caseType: 'problem',
      state: 'new'
    };

    if (formData.customerId) submitData.customerId = formData.customerId;
    if (formData.engagementId) submitData.engagementId = formData.engagementId;
    // Handle resource: either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      submitData.otherResourceNames = formData.otherResourceNames.trim();
    } else if (formData.resourceId) {
      submitData.resourceId = formData.resourceId;
    }
    // Backend expects 'assignedTo', not 'assignedToId'
    if (formData.assignedTo) submitData.assignedTo = formData.assignedTo;
    if (formData.assignmentGroupId) submitData.assignmentGroupId = formData.assignmentGroupId;
    if (formData.contactId) submitData.contactId = formData.contactId;

    // Review Matrix fields
    if (formData.rcaPresentedById) submitData.rcaPresentedById = formData.rcaPresentedById;
    if (formData.reportedIssue) submitData.reportedIssue = formData.reportedIssue;
    if (formData.problemDate) {
      // Convert date string to ISO 8601 datetime format at UTC noon
      // This prevents timezone-related date shifting issues
      submitData.problemDate = dateToUTCNoon(formData.problemDate);
    }
    if (formData.actualServiceImpact) submitData.actualServiceImpact = formData.actualServiceImpact;
    if (formData.briefAnalysis) submitData.briefAnalysis = formData.briefAnalysis;
    if (formData.rootCause) submitData.rootCause = formData.rootCause;
    if (formData.detailedAnalysis) submitData.detailedAnalysis = formData.detailedAnalysis;
    if (formData.correctiveActions) submitData.correctiveActions = formData.correctiveActions;
    if (formData.chronologyOfEvents) submitData.chronologyOfEvents = formData.chronologyOfEvents;

    createProblemMutation.mutate(submitData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      };

      // Clear dependent fields when parent fields change
      if (name === 'customerId') {
        updated.engagementId = '';
        updated.resourceId = '';
        updated.otherResourceNames = '';
        updated.contactId = '';
        setSelectedResourceIdValue('');
        setIsOtherResource(false);
      }
      if (name === 'engagementId') {
        updated.resourceId = '';
        updated.otherResourceNames = '';
        updated.contactId = '';
        setSelectedResourceIdValue('');
        setIsOtherResource(false);
      }
      if (name === 'assignmentGroupId') {
        console.log('Assignment group changed to:', value);
        updated.assignedTo = '';
      }

      return updated;
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to create problems.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/problems')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Problems
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Create New Problem</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Row 1: Number and State */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value=""
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value="New"
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Impact and Urgency */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="impact" className="block text-sm font-medium text-gray-700 mb-1">
                      Impact <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="impact"
                      name="impact"
                      value={formData.impact}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="high_1">High</option>
                      <option value="medium_2">Medium</option>
                      <option value="low_3">Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                      Urgency <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="high_1">High</option>
                      <option value="medium_2">Medium</option>
                      <option value="low_3">Low</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Priority (Auto-calculated) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (Auto-calculated)
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={(() => {
                        const priority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
                        switch (priority) {
                          case 'critical_1': return '1 - Critical';
                          case 'high_2': return '2 - High';
                          case 'moderate_3': return '3 - Moderate';
                          case 'low_4': return '4 - Low';
                          case 'planning_5': return '5 - Planning';
                          default: return '3 - Moderate';
                        }
                      })()}
                      disabled
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Customer and Engagement */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">
                      Customer <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.customerId}
                      onChange={(value: string | null) => {
                        setFormData(prev => ({ ...prev, customerId: value || '', engagementId: '', resourceId: '', contactId: '' }));
                        if (errors.customerId) {
                          setErrors(prev => ({ ...prev, customerId: '' }));
                        }
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                            errors.customerId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                          }`}
                          displayValue={(customerId: string) => {
                            const customer = (customersData?.customers || []).find((c: any) => c.id === customerId);
                            return customer ? customer.accountName : '';
                          }}
                          onChange={(event) => setCustomerQuery(event.target.value)}
                          placeholder="Search customers..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setCustomerQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(customersData?.customers || [])
                              .filter((customer: any) => customer && customer.id && customer.accountName)
                              .filter((customer: any) =>
                                customerQuery === '' ||
                                customer.accountName.toLowerCase().includes(customerQuery.toLowerCase())
                              )
                              .map((customer: any) => (
                                <Combobox.Option
                                  key={customer.id}
                                  value={customer.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {customer.accountName}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.customerId && (
                      <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="engagementId" className="block text-sm font-medium text-gray-700 mb-1">
                      Engagement <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.engagementId}
                      onChange={(value: string | null) => {
                        setFormData(prev => ({ ...prev, engagementId: value || '', resourceId: '', contactId: '' }));
                        if (errors.engagementId) {
                          setErrors(prev => ({ ...prev, engagementId: '' }));
                        }
                      }}
                      disabled={!formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                            errors.engagementId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                          }`}
                          displayValue={(engagementId: string) => {
                            const engagement = (engagementsData?.engagements || []).find((e: any) => e.id === engagementId);
                            return engagement ? `${engagement.name} - ${engagement.status}` : '';
                          }}
                          onChange={(event) => setEngagementQuery(event.target.value)}
                          placeholder={!formData.customerId ? 'Select Customer first' : 'Search engagements...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setEngagementQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(engagementsData?.engagements || [])
                              .filter((engagement: any) => engagement && engagement.id && engagement.name)
                              .filter((engagement: any) =>
                                engagementQuery === '' ||
                                engagement.name.toLowerCase().includes(engagementQuery.toLowerCase())
                              )
                              .map((engagement: any) => (
                                <Combobox.Option
                                  key={engagement.id}
                                  value={engagement.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {engagement.name} - {engagement.status}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.engagementId && (
                      <p className="mt-1 text-sm text-red-600">{errors.engagementId}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Contact */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="contactId" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.contactId}
                      onChange={(value: string | null) => {
                        setFormData({ ...formData, contactId: value || '' });
                        if (errors.contactId) {
                          setErrors(prev => ({ ...prev, contactId: '' }));
                        }
                      }}
                      disabled={!formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                          displayValue={(userId: string) => {
                            const user = (contactsData?.users || []).find((u: any) => u.id === userId);
                            return user ? `${user.firstName} ${user.lastName}` : '';
                          }}
                          onChange={(event) => setCallerQuery(event.target.value)}
                          placeholder={!formData.customerId ? 'Select Customer first' : 'Search contacts...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setCallerQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {contactsData?.users && contactsData.users.length === 0 && callerQuery !== '' ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No contacts found.
                              </div>
                            ) : contactsData?.users && contactsData.users.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No contacts available for this customer.
                              </div>
                            ) : (
                              (contactsData?.users || []).map((user: any) => (
                                <Combobox.Option
                                  key={user.id}
                                  value={user.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {user.firstName} {user.lastName} ({user.email})
                                      </span>
                                      {selected && (
                                        <span
                                          className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}
                                        >
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 7: Resource and Resource ID / Custom Resources */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="resourceId" className="block text-sm font-medium text-gray-700 mb-1">
                      Resource <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={isOtherResource ? '__OTHER__' : formData.resourceId}
                      onChange={(value: string | null) => {
                        if (value === '__OTHER__') {
                          // User selected "Others"
                          setIsOtherResource(true);
                          setFormData(prev => ({ ...prev, resourceId: '', otherResourceNames: '' }));
                          setSelectedResourceIdValue('');
                        } else {
                          // User selected a normal resource
                          setIsOtherResource(false);
                          setFormData(prev => ({ ...prev, resourceId: value || '', otherResourceNames: '' }));
                          const selectedResource = (resourcesData?.resources || []).find((r: any) => r.id === value);
                          setSelectedResourceIdValue(selectedResource?.resourceId || '');
                        }
                        if (errors.resourceId) {
                          setErrors(prev => ({ ...prev, resourceId: '' }));
                        }
                        if (errors.otherResourceNames) {
                          setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                        }
                      }}
                      disabled={!formData.engagementId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                            errors.resourceId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                          }`}
                          displayValue={(resourceId: string) => {
                            if (isOtherResource || resourceId === '__OTHER__') return 'Others (specify custom resources)';
                            const resource = (resourcesData?.resources || []).find((r: any) => r.id === resourceId);
                            return resource ? resource.name : '';
                          }}
                          onChange={(event) => setResourceQuery(event.target.value)}
                          placeholder={!formData.engagementId ? 'Select Engagement first' : 'Search resources...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setResourceQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(resourcesData?.resources || [])
                              .filter((resource: any) => resource && resource.id && resource.name)
                              .filter((resource: any) =>
                                resourceQuery === '' ||
                                resource.name.toLowerCase().includes(resourceQuery.toLowerCase())
                              )
                              .map((resource: any) => (
                                <Combobox.Option
                                  key={resource.id}
                                  value={resource.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected && !isOtherResource ? 'font-semibold' : 'font-normal'}`}>
                                        {resource.name}
                                      </span>
                                      {selected && !isOtherResource && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                            {/* Others option - always shown at the end */}
                            <Combobox.Option
                              key="__OTHER__"
                              value="__OTHER__"
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${
                                  active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                }`
                              }
                            >
                              {({ active, selected }) => (
                                <>
                                  <span className={`block truncate ${selected || isOtherResource ? 'font-semibold' : 'font-normal'}`}>
                                    Others (specify custom resources)
                                  </span>
                                  {isOtherResource && (
                                    <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.resourceId && (
                      <p className="mt-1 text-sm text-red-600">{errors.resourceId}</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Conditional: Show either Resource ID (read-only) OR Custom Resources (editable) */}
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isOtherResource ? (
                        <>Custom Resources <span className="text-red-500">*</span></>
                      ) : (
                        'Resource ID'
                      )}
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isOtherResource ? (
                      <input
                        type="text"
                        value={formData.otherResourceNames}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, otherResourceNames: e.target.value }));
                          if (errors.otherResourceNames) {
                            setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                          }
                        }}
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                          errors.otherResourceNames ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                        }`}
                        placeholder="Enter resource names separated by commas"
                      />
                    ) : (
                      <input
                        type="text"
                        value={selectedResourceIdValue}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 truncate"
                        placeholder="Select a resource to view Resource ID"
                        title={selectedResourceIdValue}
                      />
                    )}
                  </div>
                </div>
                {/* Helper text and error below the grid to maintain alignment */}
                {isOtherResource && (
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-3"></div>
                    <div className="col-span-9">
                      <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                      {errors.otherResourceNames && (
                        <p className="mt-1 text-sm text-red-600">{errors.otherResourceNames}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Row 8: Assignment Group and Assigned To */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignmentGroupId" className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Group <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignmentGroupId}
                      onChange={(value: string | null) => {
                        setFormData(prev => ({ ...prev, assignmentGroupId: value || '', assignedTo: '' }));
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(groupId: string) => {
                            const group = (assignmentGroupsData || []).find((g: any) => (g.id || g._id) === groupId);
                            return group ? group.name : '';
                          }}
                          onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                          placeholder="Search assignment groups..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignmentGroupQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(assignmentGroupsData || [])
                              .filter((group: any) => group && (group.id || group._id) && group.name)
                              .filter((group: any) =>
                                assignmentGroupQuery === '' ||
                                group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase())
                              )
                              .map((group: any) => (
                                <Combobox.Option
                                  key={group.id || group._id}
                                  value={group.id || group._id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {group.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignedTo}
                      onChange={(value: string | null) => {
                        setFormData(prev => ({ ...prev, assignedTo: value || '' }));
                        if (errors.assignedTo) {
                          setErrors(prev => ({ ...prev, assignedTo: '' }));
                        }
                      }}
                      disabled={!formData.assignmentGroupId || isLoadingMembers}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                            errors.assignedTo ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                          }`}
                          displayValue={(memberId: string) => {
                            const member = (groupMembersData || []).find((m: any) => m.id === memberId);
                            return member ? `${member.firstName} ${member.lastName}` : '';
                          }}
                          onChange={(event) => setAssignedToQuery(event.target.value)}
                          placeholder={isLoadingMembers ? 'Loading members...' : !formData.assignmentGroupId ? 'Select Assignment Group first' : 'Search assignees...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignedToQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(groupMembersData || [])
                              .filter((member: any) => member && member.id && member.firstName)
                              .filter((member: any) =>
                                assignedToQuery === '' ||
                                `${member.firstName} ${member.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase())
                              )
                              .map((member: any) => (
                                <Combobox.Option
                                  key={member.id}
                                  value={member.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {member.firstName} {member.lastName}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignedTo && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                    )}
                    {membersError && (
                      <p className="mt-1 text-sm text-red-600">Error loading group members</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Short Description (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1">
                      Short Description <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.shortDescription || ''}
                      onChange={(value) => {
                        setFormData({ ...formData, shortDescription: value });
                        if (errors.shortDescription) {
                          setErrors(prev => ({ ...prev, shortDescription: '' }));
                        }
                      }}
                      placeholder="Brief summary of the problem"
                      minHeight="80px"
                    />
                    {errors.shortDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Problem Statement (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="problemStatement" className="block text-sm font-medium text-gray-700 mb-1">
                      Problem Statement <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.problemStatement || ''}
                      onChange={(value) => {
                        setFormData({ ...formData, problemStatement: value });
                        if (errors.problemStatement) {
                          setErrors(prev => ({ ...prev, problemStatement: '' }));
                        }
                      }}
                      placeholder="Specific problem statement for better tracking"
                      minHeight="120px"
                    />
                    {errors.problemStatement && (
                      <p className="mt-1 text-sm text-red-600">{errors.problemStatement}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Review Matrix Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Review Matrix</h2>

              {/* Row 1: RCA Presented By and Problem Date */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label htmlFor="rcaPresentedById" className="block text-sm font-medium text-gray-700 mb-1">
                        RCA Presented By <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <Combobox
                        value={formData.rcaPresentedById}
                        onChange={(value: string | null) => {
                          setFormData(prev => ({ ...prev, rcaPresentedById: value || '' }));
                          if (errors.rcaPresentedById) {
                            setErrors(prev => ({ ...prev, rcaPresentedById: '' }));
                          }
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                              errors.rcaPresentedById ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                            }`}
                            displayValue={(userId: string) => {
                              const user = (usersData?.users || []).find((u: any) => (u.id || u._id) === userId);
                              return user ? `${user.firstName} ${user.lastName}` : '';
                            }}
                            onChange={(event) => setRcaPresentedByQuery(event.target.value)}
                            placeholder="Search users..."
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setRcaPresentedByQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {(usersData?.users || [])
                                .filter((user: any) => user && (user.id || user._id) && user.firstName)
                                .filter((user: any) =>
                                  rcaPresentedByQuery === '' ||
                                  `${user.firstName} ${user.lastName}`.toLowerCase().includes(rcaPresentedByQuery.toLowerCase())
                                )
                                .map((user: any) => (
                                  <Combobox.Option
                                    key={user.id || user._id}
                                    value={user.id || user._id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {user.firstName} {user.lastName}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                      {errors.rcaPresentedById && (
                        <p className="mt-1 text-sm text-red-600">{errors.rcaPresentedById}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label htmlFor="problemDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Problem Date <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <DateTimePickerInput
                        id="problemDate"
                        name="problemDate"
                        value={formData.problemDate}
                        onChange={(val) => {
                          setFormData(prev => ({ ...prev, problemDate: val }));
                          if (errors.problemDate) {
                            setErrors(prev => ({ ...prev, problemDate: '' }));
                          }
                        }}
                        max={getToday()}
                        showTimeSelect={false}
                        error={!!errors.problemDate}
                        className="w-full"
                      />
                      {errors.problemDate && (
                        <p className="mt-1 text-sm text-red-600">{errors.problemDate}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Reported Issue (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="reportedIssue" className="block text-sm font-medium text-gray-700 mb-1">
                        Reported Issue <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.reportedIssue || ''}
                        onChange={(value) => {
                          setFormData({ ...formData, reportedIssue: value });
                          if (errors.reportedIssue) {
                            setErrors(prev => ({ ...prev, reportedIssue: '' }));
                          }
                        }}
                        placeholder="Describe the reported issue..."
                        minHeight="120px"
                      />
                      {errors.reportedIssue && (
                        <p className="mt-1 text-sm text-red-600">{errors.reportedIssue}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Actual Service Impact (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="actualServiceImpact" className="block text-sm font-medium text-gray-700 mb-1">
                        Actual Service Impact <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.actualServiceImpact || ''}
                        onChange={(value) => {
                          setFormData({ ...formData, actualServiceImpact: value });
                          if (errors.actualServiceImpact) {
                            setErrors(prev => ({ ...prev, actualServiceImpact: '' }));
                          }
                        }}
                        placeholder="Describe the actual service impact..."
                        minHeight="120px"
                      />
                      {errors.actualServiceImpact && (
                        <p className="mt-1 text-sm text-red-600">{errors.actualServiceImpact}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4: Brief Analysis (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="briefAnalysis" className="block text-sm font-medium text-gray-700 mb-1">
                        Brief Analysis <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.briefAnalysis || ''}
                        onChange={(value) => {
                          setFormData({ ...formData, briefAnalysis: value });
                          if (errors.briefAnalysis) {
                            setErrors(prev => ({ ...prev, briefAnalysis: '' }));
                          }
                        }}
                        placeholder="Provide a brief analysis..."
                        minHeight="120px"
                      />
                      {errors.briefAnalysis && (
                        <p className="mt-1 text-sm text-red-600">{errors.briefAnalysis}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 5: Root Cause (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="rootCause" className="block text-sm font-medium text-gray-700 mb-1">
                        Root Cause <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.rootCause || ''}
                        onChange={(value) => {
                          setFormData({ ...formData, rootCause: value });
                          if (errors.rootCause) {
                            setErrors(prev => ({ ...prev, rootCause: '' }));
                          }
                        }}
                        placeholder="Describe the root cause of the problem..."
                        minHeight="120px"
                      />
                      {errors.rootCause && (
                        <p className="mt-1 text-sm text-red-600">{errors.rootCause}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 6: Detailed Analysis (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="detailedAnalysis" className="block text-sm font-medium text-gray-700 mb-1">
                        Detailed Analysis <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.detailedAnalysis || ''}
                        onChange={(value) => {
                          setFormData({ ...formData, detailedAnalysis: value });
                          if (errors.detailedAnalysis) {
                            setErrors(prev => ({ ...prev, detailedAnalysis: '' }));
                          }
                        }}
                        placeholder="Provide detailed analysis..."
                        minHeight="150px"
                      />
                      {errors.detailedAnalysis && (
                        <p className="mt-1 text-sm text-red-600">{errors.detailedAnalysis}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 7: Corrective/Mitigate Actions (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="correctiveActions" className="block text-sm font-medium text-gray-700 mb-1">
                        Corrective/Mitigate Actions <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.correctiveActions || ''}
                        onChange={(value) => {
                          setFormData({ ...formData, correctiveActions: value });
                          if (errors.correctiveActions) {
                            setErrors(prev => ({ ...prev, correctiveActions: '' }));
                          }
                        }}
                        placeholder="Describe corrective or mitigating actions..."
                        minHeight="120px"
                      />
                      {errors.correctiveActions && (
                        <p className="mt-1 text-sm text-red-600">{errors.correctiveActions}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 8: Chronology of Events (full width) */}
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label htmlFor="chronologyOfEvents" className="block text-sm font-medium text-gray-700 mb-1">
                        Chronology of Events <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <RichTextEditor
                        value={formData.chronologyOfEvents || ''}
                        onChange={(value) => {
                          setFormData({ ...formData, chronologyOfEvents: value });
                          if (errors.chronologyOfEvents) {
                            setErrors(prev => ({ ...prev, chronologyOfEvents: '' }));
                          }
                        }}
                        placeholder="Describe the chronology of events..."
                        minHeight="150px"
                      />
                      {errors.chronologyOfEvents && (
                        <p className="mt-1 text-sm text-red-600">{errors.chronologyOfEvents}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/problems')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createProblemMutation.isPending}
                className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {createProblemMutation.isPending ? 'Creating...' : 'Create Problem'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProblemCreate;
